import React from 'react';
import { Sort, SortValues } from '../../types/ui';

function useSort<TSortableKey extends string>(
  sortableKeys: readonly TSortableKey[],
  {
    defaultSort = { order: 'asc', orderBy: null },
    onSort: onSortParam,
  }: {
    defaultSort?: SortValues<TSortableKey>;
    onSort?: (sort: SortValues<TSortableKey>) => void;
  } = {},
) {
  const [sort, setSort] = React.useState<SortValues<TSortableKey>>(defaultSort);

  const onSort = React.useCallback(
    (orderBy: (typeof sort)['orderBy']) => {
      const isValidKey =
        !!orderBy && (!sortableKeys || sortableKeys.includes(orderBy));
      if (!isValidKey) return;

      const isCurrentlyAsc = sort.orderBy === orderBy && sort.order === 'asc';
      const order = isCurrentlyAsc ? 'desc' : 'asc';

      setSort(prev => ({
        ...prev,
        order,
        orderBy,
      }));
      onSortParam?.({ order, orderBy });
    },
    [onSortParam, sort.order, sort.orderBy, sortableKeys],
  );

  const sortMemo = React.useMemo<Sort<TSortableKey>>(
    () => ({
      ...sort,
      sortableKeys,
      onSort: onSort as Sort<TSortableKey>['onSort'],
    }),
    [onSort, sort, sortableKeys],
  );

  return sortMemo;
}

export default useSort;
